import { useEffect, useState, useRef } from 'react';
import { bool, array, object, number, func, string } from 'prop-types';

import { HeadSEO } from 'components/base';

import { isArrayEqual } from 'utils/helpers';

import { IS_NNORMAL, IS_CAMPER } from 'utils/constants/system';
import styles from './style.module.css';
import { getDomainValue } from 'utils/domain';

const defaultTileProvider = {
  url: 'https://{s}.tile.jawg.io/jawg-light/{z}/{x}/{y}{r}.png?access-token={accessToken}',
  config: {
    attribution: null, // '<a href="http://jawg.io" title="Tiles Courtesy of Jawg Maps" target="_blank">&copy; <b>Jawg</b>Maps</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    minZoom: 0,
    maxZoom: 22,
    accessToken: process.env.LEAFLET_TOKEN,
  },
};
const defaultMarkerProvider = {
  iconUrl: getDomainValue({
    camper: '/assets-new/marker-icon.png',
    nnormal: '/assets-new/selected.png',
    camperlab: '/assets-new/marker-icon.png',
  }),
};
const defaultSelectedMarkerProvider = {
  iconUrl: getDomainValue({
    camper: '/assets-new/marker-icon.png',
    nnormal: '/assets-new/selected-marker.png',
    camperlab: '/assets-new/marker-icon.png',
  }),
};
const MapComponent = ({
  visible,
  viewStoreInMap,
  pins,
  sticky = false,
  center,
  tileProvider = defaultTileProvider,
  markerProvider = defaultMarkerProvider,
  selectedMarkerProvider = defaultSelectedMarkerProvider,
  zoom = 15,
  onClickMarker,
  fitToBounds = false,
  isMobile = false,
  className = '',
  onZoom,
}) => {
  const [map, setMap] = useState(null);
  const MapConstructor = useRef(null);

  function onTwoFingerDrag(e, mapRef) {
    if (e.type === 'touchstart' && e.touches.length === 2) {
      mapRef.dragging.enable();
    } else {
      mapRef.dragging.disable();
    }
  }

  const handleGetLeaflet = async (callback) => {
    import('leaflet').then((mod) => {
      callback(mod);
    });
  };

  useEffect(() => {
    const onZoomEnd = (e) => {
      onZoom?.(e.target.getZoom());
    };
    if (map && onZoom) {
      map.on('zoomend', onZoomEnd);
    }
    return () => {
      if (map && onZoom) {
        map.off('zoomend', onZoomEnd);
      }
    };
  }, [map]);

  const generateMap = (Leaflet, origin, storesCoords) => {
    const newMap = new Leaflet.Map(styles.map, {
      center: origin,
      zoom,
    });

    if (storesCoords.length > 1 && fitToBounds) {
      const xCoords = storesCoords.map((item) => item[0]);
      const yCoords = storesCoords.map((item) => item[1]);

      const [maxX, minX] = [Math.max(...xCoords), Math.min(...xCoords)];
      const [maxY, minY] = [Math.max(...yCoords), Math.min(...yCoords)];

      const marginX = 0.5 * (maxX - minX);
      const marginY = 0.5 * (maxY - minY);

      const fitBoundsParams = [
        [minX - marginX, minY - marginY],
        [maxX + marginX, maxY + marginY],
      ];

      newMap.fitBounds(fitBoundsParams);
    }

    Leaflet.tileLayer(tileProvider.url, tileProvider.config).addTo(newMap);

    // El icono de camper tiene un Width: 22px | Height: 32px
    const markerProps = {
      iconSize: [22, 32],
      iconAnchor: [11, 32],
    };

    for (let i = 0; i < storesCoords.length; i++) {
      const isSelected = isArrayEqual(storesCoords[i], center);
      const sourceForMarker = isSelected ? selectedMarkerProvider : markerProvider;

      const markerConfig = {
        icon: Leaflet.icon({
          ...markerProps,
          ...sourceForMarker,
        }),
      };

      Leaflet.marker(
        storesCoords[i],
        IS_NNORMAL ? markerConfig
        : isSelected ? undefined
        : markerConfig,
      )
        .on('click', () => onClickMarker?.(storesCoords[i]))
        .addTo(newMap);
    }

    if (MapConstructor.current === null) {
      MapConstructor.current = Leaflet;
    }

    if (isMobile) {
      const mapEl = document.querySelector('.map');
      newMap.dragging.disable();
      mapEl.addEventListener('touchstart', (e) => onTwoFingerDrag(e, newMap));
      mapEl.addEventListener('touchend', (e) => onTwoFingerDrag(e, newMap));
    }

    setMap(newMap);
  };

  const handleRemoveMap = () => {
    if (map && map.getContainer && map.getContainer().firstChild !== null && map.off && map.remove) {
      map.off();
      map.remove();
    }
  };

  const initMap = () => {
    const averagePointCoords =
      pins.length ?
        pins.reduce((a, b) => {
          const { longitude: xA, latitude: yA } = a;
          const { longitude: xB, latitude: yB } = b;
          return { longitude: (xA + xB) / 2, latitude: (yA + yB) / 2 };
        })
      : { longitude: 0, latitude: 0 };
    const storesCoords = pins.map(({ latitude, longitude }) => [latitude, longitude]);
    const mapCenter = [averagePointCoords.latitude, averagePointCoords.longitude];

    if (MapConstructor.current === null) {
      handleGetLeaflet((mod) => {
        generateMap(mod.default, center || mapCenter, storesCoords, 15);
      });
    } else {
      handleRemoveMap();
      generateMap(MapConstructor.current, center || mapCenter, storesCoords, 15);
    }
  };

  useEffect(() => {
    if (!viewStoreInMap) {
      if (visible) {
        const changedCenter =
          center && center.length && map?.options?.center?.length ? center[0] !== map?.options?.center[0] && center[1] !== map?.options?.center[1] : false;
        if (changedCenter) {
          map.setView(center, zoom, {});
          map.invalidateSize(true);
        }

        initMap();
      } else if (map !== null) {
        handleRemoveMap();
      }
    }
  }, [visible, center, pins]);

  useEffect(() => {
    if (Array.isArray(viewStoreInMap)) {
      // el centro y el único pin son lo mismo
      generateMap(MapConstructor.current, viewStoreInMap, [viewStoreInMap], 20);
    }
  }, [viewStoreInMap]);

  return (
    <>
      <HeadSEO
        scripts={[
          <link
            key="leaflet-css"
            rel="stylesheet"
            href="https://unpkg.com/leaflet@1.7.1/dist/leaflet.css"
            integrity="sha512-xodZBNTC5n17Xt2atTPuE1HxjVMSvLVW9ocqUKLsCC5CXdbqCmblAshOMAS6/keqq/sMZMZ19scR4PsZChSR7A=="
            crossOrigin=""
          />,
          <script
            key="leaflet-js"
            src="https://unpkg.com/leaflet@1.7.1/dist/leaflet.js"
            integrity="sha512-XQoYMqMTK8LvdxXYG3nZ448hOEQiglfqkJs1NOQV44cWnUrBc8PkAOcXy20w0vlaXaVUearIOBhiXZ5V3ynxwA=="
            crossOrigin=""
          ></script>,
        ]}
      />
      <div id={styles.map} className={`map ${visible ? '' : 'escondido'} ${sticky && styles.sticky} ${className}`}></div>
    </>
  );
};

export default MapComponent;
